import { useCallback, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import ParseJSONService from 'services/jsonParser/jsonParserService';
import type { EmailPreview } from 'services/jsonParser/types';
import ParseEventStreamService from 'services/streamParser/streamParserService';
import type { StreamEvent } from 'services/streamParser/types';

import { craftEmailAgentApi } from './api';
import { craftEmailQueryKeys } from './queryKeys';
import type {
  ApplyCompetitorsRequest,
  CraftEmailCompetitor,
  CraftEmailTimelineItem,
  CreateCraftEmailThreadRequest,
  SendMessageRequest,
} from './types';
import { useSSEStream } from './useSSEStream';

const EMPTY_STREAM_EMAIL_PREVIEW: EmailPreview = {
  subject: '',
  bodyBefore: '',
  bodyAfter: '',
  chartJson: null,
};

export const useCreateCraftEmailThread = (payload: CreateCraftEmailThreadRequest | null) => {
  const { t } = useTranslation();
  const [threadId, setThreadId] = useState('');
  const [messageId, setMessageId] = useState('');
  const [email, setEmail] = useState<EmailPreview | null>(null);
  const [emailPreviews, setEmailPreviews] = useState<EmailPreview[]>([]);
  const [competitors, setCompetitors] = useState<CraftEmailCompetitor[]>([]);
  const [messagePayload, setMessagePayload] = useState<(SendMessageRequest & { seq: number }) | null>(null);
  const [competitorsPayload, setCompetitorsPayload] = useState<(ApplyCompetitorsRequest & { seq: number }) | null>(
    null
  );
  const [timeline, setTimeline] = useState<CraftEmailTimelineItem[]>([]);
  const [scrollToTopAfterUserSend, setScrollToTopAfterUserSend] = useState(0);
  const messageSeqRef = useRef(0);
  const competitorsSeqRef = useRef(0);
  const timelineEmailSeqRef = useRef(0);

  const resetState = useCallback(() => {
    setThreadId('');
    setMessageId('');
    setEmail(null);
    setEmailPreviews([]);
    setCompetitors([]);
    setMessagePayload(null);
    setCompetitorsPayload(null);
    setTimeline([]);
    setScrollToTopAfterUserSend(0);
    timelineEmailSeqRef.current = 0;
  }, []);

  const handleStreamEvent = useCallback((event: StreamEvent) => {
    if (ParseEventStreamService.isInfoStreamEvent(event)) {
      setThreadId(event.data.threadId);
    }

    const aiTextChunk = ParseEventStreamService.getAiTextStreamChunk(event);
    if (aiTextChunk) {
      const { id, content } = aiTextChunk;
      setTimeline((prev) => {
        const index = prev.findIndex((m) => m.kind === 'chat' && m.id === id && m.role === 'assistant');
        if (index >= 0) {
          const next = [...prev];
          next[index] = { kind: 'chat', id, role: 'assistant', content };
          return next;
        }
        return [...prev, { kind: 'chat', id, role: 'assistant', content }];
      });
    }

    if (ParseEventStreamService.isEmailStreamEvent(event)) {
      const chunk = event.data[0].content;
      const id = event.data[0].id;
      if (id) {
        setMessageId(id);
        const [extractedThreadId] = id.split('/');
        if (extractedThreadId) {
          setThreadId((prev) => prev || extractedThreadId);
        }
      }
      const parsedPreview = ParseJSONService.parseEmailPreview(chunk);
      setEmail(parsedPreview);
      const timelinePreview = parsedPreview ?? EMPTY_STREAM_EMAIL_PREVIEW;

      const streamId = id ?? '';
      let allocatedLocalEmailSeq: number | undefined;
      setTimeline((prev) => {
        const last = prev[prev.length - 1];
        if (last?.kind === 'email' && streamId && last.streamMessageId === streamId) {
          const next = [...prev];
          next[next.length - 1] = { ...last, preview: timelinePreview };
          return next;
        }
        if (last?.kind === 'email' && !streamId && last.streamMessageId === null) {
          const next = [...prev];
          next[next.length - 1] = { ...last, preview: timelinePreview };
          return next;
        }
        let itemId: string;
        if (streamId) {
          itemId = `email-${streamId}`;
        } else {
          if (allocatedLocalEmailSeq === undefined) {
            timelineEmailSeqRef.current += 1;
            allocatedLocalEmailSeq = timelineEmailSeqRef.current;
          }
          itemId = `email-local-${allocatedLocalEmailSeq}`;
        }
        return [...prev, { kind: 'email' as const, id: itemId, streamMessageId: id || null, preview: timelinePreview }];
      });

      const domains = (parsedPreview?.chartJson as { xargs?: { domains?: CraftEmailCompetitor[] } } | undefined)?.xargs
        ?.domains;
      if (domains?.length) {
        setCompetitors(domains);
      }
    }

    if (ParseEventStreamService.isEmailPreviewStreamEvent(event)) {
      const chunk = event.data[0].content;
      const parsedEmailPreviews = ParseJSONService.parseAIEmailPreviews(chunk);
      setEmailPreviews(parsedEmailPreviews.filter((p) => p.subject));
    }

    if (ParseEventStreamService.isEmailCompetitorsStreamEvent(event)) {
      try {
        const chunk = event.data[0].content;
        const parsed = JSON.parse(chunk) as CraftEmailCompetitor[];
        setCompetitors(parsed);
      } catch {
        // ignore malformed competitors data
      }
    }

    if (ParseEventStreamService.isUnknownStreamEvent(event)) {
      const errorPreview: EmailPreview = {
        subject: `${event.data.error}: ${event.data.message}`,
        bodyBefore: '',
        bodyAfter: '',
        chartJson: null,
      };
      setEmail(errorPreview);
      timelineEmailSeqRef.current += 1;
      const errorSeq = timelineEmailSeqRef.current;
      setTimeline((prev) => [
        ...prev,
        {
          kind: 'email',
          id: `email-error-${errorSeq}`,
          streamMessageId: null,
          preview: errorPreview,
        },
      ]);
    }
  }, []);

  const {
    isFetching: isCreating,
    isError: isCreateError,
    stop: stopCreate,
    refetch: refetchCreate,
  } = useSSEStream<CreateCraftEmailThreadRequest>({
    enabled: !!payload,
    queryKey: craftEmailQueryKeys.createThread(payload),
    payload: payload ?? undefined,
    fetcher: craftEmailAgentApi.createThread,
    onEvent: handleStreamEvent,
  });

  const {
    isFetching: isMessaging,
    isError: isMessageError,
    stop: stopMessage,
  } = useSSEStream<SendMessageRequest>({
    enabled: !!messagePayload,
    queryKey: craftEmailQueryKeys.sendMessage(messagePayload),
    payload: messagePayload ?? undefined,
    fetcher: craftEmailAgentApi.sendMessage,
    onEvent: handleStreamEvent,
  });

  const {
    isFetching: isApplyingCompetitors,
    isError: isApplyCompetitorsError,
    stop: stopApplyCompetitors,
  } = useSSEStream<ApplyCompetitorsRequest>({
    enabled: !!competitorsPayload,
    queryKey: craftEmailQueryKeys.applyCompetitors(competitorsPayload),
    payload: competitorsPayload ?? undefined,
    fetcher: craftEmailAgentApi.applyCompetitors,
    onEvent: handleStreamEvent,
  });

  const addUserMessage = useCallback((message: string) => {
    messageSeqRef.current += 1;
    const seq = messageSeqRef.current;
    setTimeline((prev) => [...prev, { kind: 'chat', id: `user-${seq}`, role: 'user', content: message }]);
    setScrollToTopAfterUserSend((n) => n + 1);
  }, []);

  const sendMessage = useCallback(
    (message: string) => {
      if (!threadId || !message.trim()) {
        return;
      }
      const trimmed = message.trim();
      addUserMessage(trimmed);
      setMessagePayload({ threadId, message: trimmed, seq: messageSeqRef.current });
    },
    [threadId, addUserMessage]
  );

  const applyPreview = useCallback(
    (preview: EmailPreview) => {
      setEmail(preview);
      let allocatedAppliedSeq: number | undefined;
      setTimeline((prev) => {
        if (allocatedAppliedSeq === undefined) {
          timelineEmailSeqRef.current += 1;
          allocatedAppliedSeq = timelineEmailSeqRef.current;
        }
        return [
          ...prev,
          {
            kind: 'email' as const,
            id: `email-applied-${allocatedAppliedSeq}`,
            streamMessageId: null,
            preview,
          },
        ];
      });
      if (threadId) {
        const rawContent = [preview.subject, preview.bodyBefore, preview.bodyAfter].filter(Boolean).join('\n');
        void craftEmailAgentApi.applyEmailPreview(threadId, rawContent);
      }
    },
    [threadId]
  );

  const applyCompetitors = useCallback(
    (newCompetitors: CraftEmailCompetitor[]) => {
      if (!threadId) {
        return;
      }
      messageSeqRef.current += 1;
      const seq = messageSeqRef.current;
      setTimeline((prev) => [
        ...prev,
        {
          kind: 'chat',
          id: `user-${seq}`,
          role: 'user',
          content: t('company.overview.ai-outreach.chip_replace_competitors', 'Replace competitors'),
        },
      ]);
      setScrollToTopAfterUserSend((n) => n + 1);
      setCompetitors(newCompetitors);
      setEmailPreviews([]);
      competitorsSeqRef.current += 1;
      setCompetitorsPayload({ threadId, competitors: newCompetitors, seq: competitorsSeqRef.current });
    },
    [threadId, t]
  );

  const stop = useCallback(() => {
    void stopCreate();
    void stopMessage();
    void stopApplyCompetitors();
  }, [stopCreate, stopMessage, stopApplyCompetitors]);

  const isFollowUpLoading = isMessaging || isApplyingCompetitors;

  return {
    threadId,
    messageId,
    email,
    emailPreviews,
    competitors,
    timeline,
    scrollToTopAfterUserSend,
    isCreating,
    isMessaging,
    isApplyingCompetitors,
    isInitialLoading: isCreating,
    isFollowUpLoading,
    isLoading: isCreating || isMessaging || isApplyingCompetitors,
    isError: isCreateError || isMessageError || isApplyCompetitorsError,
    refetchCreate,
    stop,
    reset: resetState,
    addUserMessage,
    sendMessage,
    applyPreview,
    applyCompetitors,
  };
};
